import { Button, makeStyles, Menu, MenuItem, Tooltip, Typography } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import classNames from 'classnames';
import React from 'react';

import { ReactComponent as AddFaqSvg } from '../../../../assets/add_faq.svg';
import { ReactComponent as CreatePostSvg } from '../../../../assets/create_post.svg';
import { useGetLoggedInUserQuery } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { useDialogState } from '../useDialogState';

const NEW_POST_TEXT = 'Create Post';
const IMPORT_GDOCS_POST_TEXT = 'Import Post from Google Docs';
const EDIT_POST_TEXT = 'Edit Post';
const EDIT_EVENT_TEXT = 'Edit Event';
const NEW_EVENT_TEXT = 'Create Event';
const IMPORT_GDOCS_EVENT_TEXT = 'Import Event from Google Calendar';
const NEW_BOARD_TEXT = 'Create Board';
const IMPORT_GDOCS_BOARD_TEXT = 'Import Board from Google Docs';
const NEW_BOARD_FROM_TEMPLATE_TEXT = 'Create Board from Template';
const EDIT_BOARD_TEXT = 'Edit Board';
const IMPORT_GDOCS_FACT_TEXT = 'Import Card from Google Docs';
const NEW_FACT_TEXT = 'Create FAQ';
const NEW_CARD_TEXT = 'Create Card';
const EDIT_FACT_TEXT = 'Update FAQ';
const EDIT_CARD_TEXT = 'Update Card';
const NEW_TASK_TEXT = 'Create Task';
const NEW_MILESTONE_TEXT = 'Create Milestone';
const NEW_RESOURCE_REVIEW_PROCESS = 'Create Review & Sign-off Milestone';
const EDIT_RESOURCE_REVIEW_PROCESS = 'Edit Review & Sign-off Milestone';

export const CreateOrEditDialogTitle = (): React.ReactElement | null => {
  const { data: userData, loading: userLoading } = useGetLoggedInUserQuery();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useDialogState();

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  if (userLoading || userData?.currentUser == null) {
    return null;
  }

  const IconSvg =
    dialogState?.type === 'new_fact' || dialogState?.type === 'edit_fact' || dialogState?.type === 'import_gdocs_fact'
      ? AddFaqSvg
      : CreatePostSvg;

  if (
    dialogState?.type === 'edit_post' ||
    dialogState?.type === 'edit_event' ||
    dialogState?.type === 'edit_fact' ||
    dialogState?.type === 'edit_card' ||
    dialogState?.type === 'edit_board' ||
    dialogState?.type === 'edit_resource_review_process' ||
    dialogState?.type === 'new_task'
  ) {
    return (
      <div className={classes.root}>
        <div className={classes.titleContainer}>
          <IconSvg className={classNames(classes.labelAdornmentIcon, classes.iconSvg)} />
          <span className={classes.label}>
            {dialogState?.type === 'new_task' && NEW_TASK_TEXT}
            {dialogState?.type === 'edit_post' && EDIT_POST_TEXT}
            {dialogState?.type === 'edit_event' && EDIT_EVENT_TEXT}
            {dialogState?.type === 'edit_fact' && EDIT_FACT_TEXT}
            {dialogState?.type === 'edit_card' && EDIT_CARD_TEXT}
            {dialogState?.type === 'edit_board' && EDIT_BOARD_TEXT}
            {dialogState?.type === 'edit_resource_review_process' && EDIT_RESOURCE_REVIEW_PROCESS}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Button
        aria-controls="post-creation-source-menu"
        aria-haspopup="true"
        classes={{ label: classes.label }}
        startIcon={<IconSvg className={classes.iconSvg} />}
        endIcon={<ArrowDropDownIcon fontSize="inherit" />}
        size="large"
        onClick={handleClick}
      >
        {dialogState?.type === 'new_post' && NEW_POST_TEXT}
        {dialogState?.type === 'import_gdocs_post' && IMPORT_GDOCS_POST_TEXT}
        {dialogState?.type === 'new_event' && NEW_EVENT_TEXT}
        {dialogState?.type === 'import_to_timeline' && IMPORT_GDOCS_EVENT_TEXT}
        {dialogState?.type === 'new_board' && NEW_BOARD_TEXT}
        {dialogState?.type === 'import_gdocs_faqs' && IMPORT_GDOCS_BOARD_TEXT}
        {dialogState?.type === 'new_board_from_template' && NEW_BOARD_FROM_TEMPLATE_TEXT}
        {dialogState?.type === 'new_fact' && NEW_FACT_TEXT}
        {dialogState?.type === 'new_card' && NEW_CARD_TEXT}
        {dialogState?.type === 'import_gdocs_fact' && IMPORT_GDOCS_FACT_TEXT}
        {dialogState?.type === 'new_milestone' && NEW_MILESTONE_TEXT}
        {dialogState?.type === 'new_resource_review_process' && NEW_RESOURCE_REVIEW_PROCESS}
      </Button>
      <Menu
        open={anchorEl != null}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      >
        {dialogState?.type === 'new_post' && (
          <MenuItemWithDisabledReason
            disabled={
              !userData.currentUser.hasExtraGSuiteScopes
                ? { reason: 'You must enable GSuite Integration to acccess this feature' }
                : undefined
            }
            onClick={(): void => {
              setDialogState({ type: 'import_gdocs_post', plexId: dialogState.plexId }, 'replace');
            }}
          >
            {IMPORT_GDOCS_POST_TEXT}
          </MenuItemWithDisabledReason>
        )}
        {dialogState?.type === 'import_gdocs_post' && (
          <MenuItem
            onClick={(): void => {
              setDialogState({ type: 'new_post', plexId: dialogState.plexId }, 'replace');
            }}
          >
            {NEW_POST_TEXT}
          </MenuItem>
        )}
        {dialogState?.type === 'new_event' && (
          <MenuItemWithDisabledReason
            disabled={
              !userData.currentUser.hasExtraGSuiteScopes
                ? { reason: 'You must enable GSuite Integration to acccess this feature' }
                : undefined
            }
            onClick={(): void => {
              setDialogState({ type: 'import_to_timeline', plexId: dialogState.plexId }, 'replace');
            }}
          >
            {IMPORT_GDOCS_EVENT_TEXT}
          </MenuItemWithDisabledReason>
        )}
        {dialogState?.type === 'import_to_timeline' && (
          <MenuItem
            onClick={(): void => {
              setDialogState({ type: 'new_event', plexId: dialogState.plexId }, 'replace');
            }}
          >
            {NEW_EVENT_TEXT}
          </MenuItem>
        )}
        {(dialogState?.type === 'new_board' ||
          dialogState?.type === 'import_gdocs_faqs' ||
          dialogState?.type === 'new_board_from_template') && [
          dialogState?.type !== 'new_board' && (
            <MenuItem
              key="new_board"
              onClick={(): void => {
                setDialogState({ type: 'new_board', plexId: dialogState.plexId }, 'replace');
              }}
            >
              {NEW_BOARD_TEXT}
            </MenuItem>
          ),
          dialogState?.type !== 'new_board_from_template' && (
            <MenuItem
              key="new_board_from_template"
              onClick={(): void => {
                setDialogState({ type: 'new_board_from_template', plexId: dialogState.plexId }, 'replace');
              }}
            >
              {NEW_BOARD_FROM_TEMPLATE_TEXT}
            </MenuItem>
          ),
          dialogState?.type !== 'import_gdocs_faqs' && (
            <MenuItemWithDisabledReason
              key="import_gdocs_faqs"
              disabled={
                !userData.currentUser.hasExtraGSuiteScopes
                  ? { reason: 'You must enable GSuite Integration to acccess this feature' }
                  : undefined
              }
              onClick={(): void => {
                setDialogState({ type: 'import_gdocs_faqs', plexId: dialogState.plexId }, 'replace');
              }}
            >
              {IMPORT_GDOCS_BOARD_TEXT}
            </MenuItemWithDisabledReason>
          ),
        ]}
        {(dialogState?.type === 'new_fact' ||
          dialogState?.type === 'new_card' ||
          dialogState?.type === 'import_gdocs_fact') && [
          dialogState?.type !== 'new_card' && (
            <MenuItem
              key="new_card"
              onClick={(): void => {
                setDialogState({ type: 'new_card', boardId: dialogState.boardId }, 'replace');
              }}
            >
              {NEW_CARD_TEXT}
            </MenuItem>
          ),
          dialogState?.type !== 'new_fact' && (
            <MenuItem
              key="new_fact"
              onClick={(): void => {
                setDialogState({ type: 'new_fact', boardId: dialogState.boardId }, 'replace');
              }}
            >
              {NEW_FACT_TEXT}
            </MenuItem>
          ),
          dialogState?.type !== 'import_gdocs_fact' && (
            <MenuItem
              key="import_gdocs_fact"
              onClick={(): void => {
                setDialogState({ type: 'import_gdocs_fact', boardId: dialogState.boardId }, 'replace');
              }}
            >
              {IMPORT_GDOCS_FACT_TEXT}
            </MenuItem>
          ),
        ]}
        {(dialogState?.type === 'new_milestone' || dialogState?.type === 'new_resource_review_process') && [
          dialogState?.type !== 'new_milestone' && (
            <MenuItem
              key="new_milestone"
              onClick={(): void => {
                setDialogState({ type: 'new_milestone', plexId: dialogState.plexId }, 'replace');
              }}
            >
              {NEW_MILESTONE_TEXT}
            </MenuItem>
          ),
          dialogState?.type !== 'new_resource_review_process' && (
            <MenuItem
              key="new_resource_review_process"
              onClick={(): void => {
                setDialogState({ type: 'new_resource_review_process', plexId: dialogState.plexId }, 'replace');
              }}
            >
              {NEW_RESOURCE_REVIEW_PROCESS}
            </MenuItem>
          ),
        ]}
      </Menu>
    </div>
  );
};

const MenuItemWithDisabledReason = React.forwardRef(function MenuItemWithDisabledReason(
  {
    children,
    onClick,
    disabled,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    disabled?: { reason: string };
  },
  ref: React.Ref<HTMLLIElement>
): React.ReactElement {
  if (disabled != null) {
    return (
      <Tooltip title={<Typography>{disabled.reason}</Typography>}>
        <div>
          {/* Extra div necessary to show tooltip due to https://github.com/mui-org/material-ui/issues/10502 */}
          <MenuItem ref={ref} disabled onClick={onClick}>
            {children}
          </MenuItem>
        </div>
      </Tooltip>
    );
  } else {
    return (
      <MenuItem ref={ref} onClick={onClick}>
        {children}
      </MenuItem>
    );
  }
});

const useStyles = makeStyles(() => ({
  root: {
    marginLeft: spacings.normal,
    marginTop: spacings.normal,
    marginBottom: spacings.xsmall,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 11px',
  },
  label: {
    fontWeight: 'bold',
    fontSize: 24,
    lineHeight: 1.75,
  },
  labelAdornmentIcon: {
    width: 24,
    height: 24,
    marginLeft: '-4px',
    marginRight: '8px',
  },
  iconSvg: {
    width: 32,
    height: 32,
    marginRight: spacings.normal,
    fill: `${colorPalette.dark._800} !important`, // sets the svg color
  },
}));
